import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useMediaQuery } from 'react-responsive'
import { Link, useLocation } from 'react-router-dom'
import { SLOTS_ROUTE_PATHS, useGoToSlot } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'

import { ISlide } from 'shared/models/Slider'
import { mediaSize } from 'shared/style/var'
import { getRouteParamsByStrings } from 'shared/utils/getRouteParamsByStrings'

import { resolveLegacySlotId } from 'modules/SlotsRoot/utils/resolveLegacySlotRedirect'

import { selectIsUserAuthenticated } from 'features/auth/selectors'
import { selectLocaleDict } from 'features/locale/selectors'

import { addNotify } from 'components/Notify'
import { Slider } from 'components/Slider'

import { useGetSlides } from './data/useGetSlides'
import { SliderPlug } from './SliderPlug/SliderPlug'

import './CarouselSlider.scss'

const b = block('carousel-slider')

const getSlotIdFromLink = (link: string) => {
  const pathname = link.startsWith('http') ? new URL(link).pathname : link.split('?')[0]
  const frameParams = getRouteParamsByStrings<{ id?: string }>(SLOTS_ROUTE_PATHS.SLOTS_FRAME, pathname)

  if (frameParams?.id && !Number.isNaN(Number(frameParams.id))) {
    return Number(frameParams.id)
  }

  const legacySectionParams = getRouteParamsByStrings<{ sectionId?: string; gameId?: string }>(
    '/slots/:sectionId/:gameId',
    pathname,
  )

  if (legacySectionParams?.gameId && legacySectionParams.sectionId !== 'frame') {
    return resolveLegacySlotId(legacySectionParams.gameId)
  }

  const legacyProviderParams = getRouteParamsByStrings<{ gameId?: string }>(
    '/slots/:gameType/:provider/:gameId',
    pathname,
  )

  if (legacyProviderParams?.gameId) {
    return resolveLegacySlotId(legacyProviderParams.gameId)
  }

  return null
}

interface ICarouselSliderProps {
  carouselClassname?: string
  slideClassname?: string
  slidesPerView?: number | 'auto'
  initialSlide?: number
  isWithPagination?: boolean
  route?: string
}

export const CarouselSlider: React.FC<ICarouselSliderProps> = ({
  route,
  isWithPagination = false,
  initialSlide = 1,
  slidesPerView = 1.5,
  carouselClassname = '',
  slideClassname = '',
}) => {
  const { pathname } = useLocation()

  const currentPathname = route ?? pathname

  const isMobileOrTablet = useMediaQuery({ maxWidth: mediaSize.tablet })

  const slides = useGetSlides({ pathname: currentPathname, isMobileOrTablet })

  const locale = useSelector(selectLocaleDict, shallowEqual).slots
  const isAuth = useSelector(selectIsUserAuthenticated, shallowEqual)
  const { linkTo, handleClickSlot } = useGoToSlot({})

  const [imageRef, setImageRef] = useState<HTMLImageElement | null>()

  const handleSlideClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, slide: ISlide, slotId: number | null) => {
      if (slotId) {
        handleClickSlot(e, slotId, 'money')
        return
      }

      if (slide.authRequired && !isAuth) {
        e.preventDefault()
        addNotify(locale.pleaseAuth)
      }
    },
    [handleClickSlot, isAuth, locale],
  )

  const slidesList = useMemo(
    () =>
      slides.currentSlides.map((item, index) => {
        const slotId = getSlotIdFromLink(item.link)

        return (
          <Link
            to={slotId ? linkTo(slotId, 'money') : item.link}
            key={`${currentPathname}-${item.id}-${item.image}`} // пожалуйста не переопределяйте этот ключ, он
            // несет важнейшее значение для перерендера слайдов (подробнее в доке)
            onClick={e => handleSlideClick(e, item, slotId)}
            className={b('slide').mix(slideClassname)}
            style={{
              paddingBottom: imageRef ? `${100 / (imageRef.naturalWidth / imageRef.naturalHeight)}%` : '',
            }}>
            <img
              src={item.image}
              alt={`${item.id}-slide`}
              onLoad={e => {
                if (index === 0) setImageRef(e.target as HTMLImageElement)
              }}
            />
          </Link>
        )
      }),
    [slides, handleSlideClick, slideClassname, imageRef, currentPathname, linkTo],
  )

  useEffect(() => {
    setImageRef(null)
  }, [currentPathname])

  if (!slides.carouselsLoading.isLoading && slides.currentSlides.length === 0) {
    return null
  }

  return (
    <div className={b.mix(carouselClassname)}>
      {!imageRef && <SliderPlug isMobileOrTablet={isMobileOrTablet} pathname={currentPathname} />}
      <div className={b('slider-container')}>
        <Slider
          items={slidesList}
          slidesPerView={isMobileOrTablet ? 1 : slidesPerView}
          initialSlide={initialSlide}
          spaceBetween={24}
          isWithPagination={isWithPagination}
        />
      </div>
    </div>
  )
}

export default CarouselSlider
